class VariantSelects extends HTMLElement {
  constructor() {
    super();
  }

  connectedCallback() {
    this.addEventListener('change', (event) => {
      const target = event.target;
      this.updateSelectionMetadata(event);

      publish(PUB_SUB_EVENTS.optionValueSelectionChange, {
        data: {
          event,
          target,
          selectedOptionValues: this.selectedOptionValues,
        },
      });
    });
  }

  updateSelectionMetadata({ target }) {
    const { value } = target;

    const selectedSwatchValue = target.closest(`.product-form__input`).querySelector('[data-selected-value]');
    if (selectedSwatchValue) selectedSwatchValue.innerHTML = value;
  }

  get selectedOptionValues() {
    return Array.from(this.querySelectorAll('select option[selected], fieldset input:checked')).map(
      ({ dataset }) => dataset.optionValueId,
    );
  }
}

class ProductInfo extends HTMLElement {
  onVariantChangeUnsubscriber = undefined;
  abortController = undefined;
  pendingRequestUrl = null;
  pendingLanguage = null;

  constructor() {
    super();
  }

  connectedCallback() {
    this.languageConfig = JSON.parse(this.querySelector('[data-variant-languages]')?.textContent || 'null');

    this.onVariantChangeUnsubscriber = subscribe(
      PUB_SUB_EVENTS.optionValueSelectionChange,
      this.handleOptionValueChange.bind(this),
    );

    // The option change handler calls into product-form, which is defined after this element.
    customElements.whenDefined('product-form').then(() => this.selectCurrentLanguage());

    this.dispatchEvent(new CustomEvent('product-info:loaded', { bubbles: true }));
  }

  disconnectedCallback() {
    this.onVariantChangeUnsubscriber();
  }

  handleOptionValueChange({ data: { event, target, selectedOptionValues } }) {
    if (!this.contains(event.target)) return;

    this.resetProductFormState();

    const productUrl =
      target.getAttribute('data-product-url') || this.pendingRequestUrl || this.getAttribute('data-url');
    this.pendingRequestUrl = productUrl;

    // Kept until a response lands, so a quick change of another option doesn't drop the language switch.
    const language = this.languageConfig?.values[target.dataset.optionValueId];
    if (language) this.pendingLanguage = language;

    this.renderProductInfo({
      requestUrl: this.buildRequestUrlWithParams(productUrl, selectedOptionValues),
      // Only a user's change gets its focus back, so selectCurrentLanguage() doesn't scroll the page on load.
      targetId: event.isTrusted ? target.id : null,
      callback: this.handleUpdateProductInfo(productUrl, this.pendingLanguage),
    });
  }

  resetProductFormState() {
    const productForm = this.productForm;
    productForm?.toggleSubmitButton(true);
  }

  renderProductInfo({ requestUrl, targetId, callback }) {
    this.abortController?.abort();
    this.abortController = new AbortController();

    fetch(requestUrl, { signal: this.abortController.signal })
      .then((response) => response.text())
      .then((responseText) => {
        this.pendingRequestUrl = null;
        this.pendingLanguage = null;
        const html = new DOMParser().parseFromString(responseText, 'text/html');
        callback(html);
      })
      .then(() => {
        // set focus to last clicked option value
        if (targetId) document.querySelector(`#${targetId}`)?.focus();
      })
      .catch((error) => {
        if (error.name === 'AbortError') {
          console.log('Fetch aborted by user');
        } else {
          console.error(error);
        }
      });
  }

  getSelectedVariant(productInfoNode) {
    const selectedVariant = productInfoNode.querySelector('variant-selects [data-selected-variant]')?.innerHTML;
    return !!selectedVariant ? JSON.parse(selectedVariant) : null;
  }

  buildRequestUrlWithParams(url, optionValues) {
    const params = [];

    params.push(`section_id=${this.sectionId}`);

    if (optionValues.length) {
      params.push(`option_values=${optionValues.join(',')}`);
    }

    return `${url}?${params.join('&')}`;
  }

  updateOptionValues(html) {
    const variantSelects = html.querySelector('variant-selects');
    if (variantSelects) {
      HTMLUpdateUtility.viewTransition(this.variantSelectors, variantSelects);
    }
  }

  handleUpdateProductInfo(productUrl, language) {
    return (html) => {
      const variant = this.getSelectedVariant(html);
      if (variant && language && language !== this.languageConfig.current) {
        this.redirectToLanguage(language, variant.id);
        return;
      }

      this.updateOptionValues(html);
      this.updateURL(productUrl, variant?.id);
      this.updateVariantInputs(variant?.id);

      if (!variant) {
        this.setUnavailable();
        return;
      }

      this.updateMedia(variant?.featured_media?.id);

      const syncAttributes = (source, destination, excludedAttributes = ['id']) => {
        const excluded = new Set(excludedAttributes);

        Array.from(source.attributes).forEach(({ name, value }) => {
          if (excluded.has(name) || destination.getAttribute(name) === value) return;
          destination.setAttribute(name, value);
        });

        Array.from(destination.attributes).forEach(({ name }) => {
          if (excluded.has(name) || source.hasAttribute(name)) return;
          destination.removeAttribute(name);
        });
      };

      const updateSourceFromDestination = (id, shouldHide = (source) => false) => {
        const source = html.getElementById(`${id}-${this.sectionId}`);
        const destination = this.querySelector(`#${id}-${this.getAttribute('data-section')}`);
        if (source && destination) {
          destination.innerHTML = source.innerHTML;
          syncAttributes(source, destination);
          destination.classList.toggle('hidden', shouldHide(source));
        }
      };

      updateSourceFromDestination('price');
      updateSourceFromDestination('Sku', ({ classList }) => classList.contains('hidden'));
      updateSourceFromDestination('Inventory', ({ innerText }) => innerText === '');
      updateSourceFromDestination('Volume');
      updateSourceFromDestination('Price-Per-Item', ({ classList }) => classList.contains('hidden'));
      updateSourceFromDestination('ProductSubmitButton');

      this.querySelector(`#Quantity-Rules-${this.getAttribute('data-section')}`)?.classList.remove('hidden');
      this.querySelector(`#Volume-Note-${this.getAttribute('data-section')}`)?.classList.remove('hidden');

      this.productForm?.toggleSubmitButton(
        html.getElementById(`ProductSubmitButton-${this.sectionId}`)?.hasAttribute('disabled') ?? true,
        window.theme.variantStrings.soldOut,
      );

      publish(PUB_SUB_EVENTS.variantChange, {
        data: {
          sectionId: this.sectionId,
          html,
          variant,
        },
      });
    };
  }

  updateVariantInputs(variantId) {
    this.querySelectorAll(
      `#product-form-${this.getAttribute('data-section')}, #product-form-installment-${this.getAttribute(
        'data-section',
      )}`,
    ).forEach((productForm) => {
      const input = productForm.querySelector('input[name="id"]');
      input.value = variantId ?? '';
      input.dispatchEvent(new Event('change', { bubbles: true }));
    });
  }

  updateURL(url, variantId) {
    if (this.getAttribute('data-update-url') === 'false') return;

    const currentUrl = new URL(window.location.href);
    if (variantId) {
      currentUrl.searchParams.set('variant', variantId);
    } else {
      currentUrl.searchParams.delete('variant');
    }
    const nextUrl = `${currentUrl.pathname}${currentUrl.search}${currentUrl.hash}`;
    window.history.replaceState({}, '', nextUrl);
  }

  // Without an explicit variant the first one wins, which may be in another language than the page.
  selectCurrentLanguage() {
    const config = this.languageConfig;
    const params = new URLSearchParams(window.location.search);
    if (!config || params.has('variant') || params.has('option_values')) return;

    const input = Array.from(this.variantSelectors.querySelectorAll('input[data-option-value-id]')).find(
      (option) =>
        config.values[option.dataset.optionValueId] === config.current && !option.classList.contains('disabled'),
    );
    if (!input || input.checked) return;

    // Not click(): the change it fires is trusted, and handleOptionValueChange would move focus to the picker.
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
  }

  // Each locale has its own root per domain (/es on .com, /en on .es), so the target comes from the
  // market's root_url instead of a hardcoded prefix. replace() keeps Back from restoring this page mid-switch.
  redirectToLanguage(language, variantId) {
    const { roots, current } = this.languageConfig;
    const trimRoot = (root) => root.replace(/\/$/, '');
    const currentRoot = trimRoot(roots[current]);
    const url = new URL(window.location.href);
    const path = url.pathname.startsWith(`${currentRoot}/`) ? url.pathname.slice(currentRoot.length) : url.pathname;
    url.pathname = trimRoot(roots[language]) + path;
    // option_values would override variant on the target page.
    url.searchParams.delete('option_values');
    url.searchParams.set('variant', variantId);
    window.location.replace(url);
  }

  setUnavailable() {
    this.productForm?.toggleSubmitButton(true, window.theme.variantStrings.unavailable);

    const selectors = ['price', 'Inventory', 'Sku', 'Price-Per-Item', 'Volume-Note', 'Volume', 'Quantity-Rules']
      .map((id) => `#${id}-${this.getAttribute('data-section')}`)
      .join(', ');
    document.querySelectorAll(selectors).forEach(({ classList }) => classList.add('hidden'));
  }

  updateMedia(variantFeaturedMediaId) {
    if (!variantFeaturedMediaId) return;

    const mediaSlide = document.querySelector(
      `[data-media-id=${this.getAttribute('data-section')}-${variantFeaturedMediaId}`,
    );

    if (!mediaSlide) return;
    const slidePosition = mediaSlide.getAttribute('data-position');
    window.productGalleryGoToSlide(slidePosition);
  }

  get productForm() {
    return this.querySelector(`product-form`);
  }

  get variantSelectors() {
    return this.querySelector('variant-selects');
  }

  get sectionId() {
    return this.getAttribute('data-original-section') || this.getAttribute('data-section');
  }
}

class ProductForm extends HTMLElement {
  constructor() {
    super();

    this.form = this.querySelector('form');
    this.variantIdInput.disabled = false;
    this.submitButton = this.querySelector('[type="submit"]');
    this.submitButtonText = this.submitButton.querySelector('span');
  }

  toggleSubmitButton(disable = true, text) {
    if (disable) {
      this.submitButton.setAttribute('disabled', 'disabled');
      if (text) this.submitButtonText.textContent = text;
    } else {
      this.submitButton.removeAttribute('disabled');
      this.submitButtonText.textContent = window.theme.variantStrings.addToCart;
    }
  }

  get variantIdInput() {
    return this.form.querySelector('[name=id]');
  }
}

class QuantityInput extends HTMLElement {
  constructor() {
    super();
    this.input = this.querySelector('input');
    this.changeEvent = new Event('change', { bubbles: true });
    this.input.addEventListener('change', this.onInputChange.bind(this));
    this.querySelectorAll('button').forEach((button) =>
      button.addEventListener('click', this.onButtonClick.bind(this)),
    );
  }

  quantityUpdateUnsubscriber = undefined;

  connectedCallback() {
    this.validateQtyRules();
    this.quantityUpdateUnsubscriber = subscribe(PUB_SUB_EVENTS.quantityUpdate, this.validateQtyRules.bind(this));
  }

  disconnectedCallback() {
    if (this.quantityUpdateUnsubscriber) {
      this.quantityUpdateUnsubscriber();
    }
  }

  onInputChange(event) {
    this.validateQtyRules();
  }

  onButtonClick(event) {
    event.preventDefault();
    const previousValue = this.input.value;

    if (event.target.name === 'plus') {
      if (parseInt(this.input.dataset.min) > parseInt(this.input.step) && this.input.value == 0) {
        this.input.value = this.input.dataset.min;
      } else {
        this.input.stepUp();
      }
    } else {
      this.input.stepDown();
    }

    if (previousValue !== this.input.value) this.input.dispatchEvent(this.changeEvent);

    if (this.input.dataset.min === previousValue && event.target.name === 'minus') {
      this.input.value = parseInt(this.input.min);
    }
  }

  validateQtyRules() {
    const value = parseInt(this.input.value);
    if (this.input.min) {
      const buttonMinus = this.querySelector(".quantity__button[name='minus']");
      buttonMinus.classList.toggle('disabled', parseInt(value) <= parseInt(this.input.min));
    }
    if (this.input.max) {
      const max = parseInt(this.input.max);
      const buttonPlus = this.querySelector(".quantity__button[name='plus']");
      buttonPlus.classList.toggle('disabled', value >= max);
    }
  }
}

class PricePerItem extends HTMLElement {
  constructor() {
    super();
    this.variantId = this.dataset.variantId;
    this.input = document.getElementById(`Quantity-${this.dataset.sectionId || this.dataset.variantId}`);
    if (this.input) {
      this.input.addEventListener('change', this.onInputChange.bind(this));
    }

    this.getVolumePricingArray();
  }

  updatePricePerItemUnsubscriber = undefined;
  variantIdChangedUnsubscriber = undefined;

  connectedCallback() {
    // Update variantId if variant is switched on product page
    this.variantIdChangedUnsubscriber = subscribe(PUB_SUB_EVENTS.variantChange, (event) => {
      this.variantId = event.data.variant.id.toString();
      this.getVolumePricingArray();
    });

    this.updatePricePerItemUnsubscriber = subscribe(PUB_SUB_EVENTS.cartUpdate, (response) => {
      if (!response.cartData) return;

      // Item was added to cart via product page
      if (response.cartData['variant_id'] !== undefined) {
        if (response.productVariantId === this.variantId) this.updatePricePerItem(response.cartData.quantity);
        // Qty was updated in cart
      } else if (response.cartData.item_count !== 0) {
        const isVariant = response.cartData.items.find((item) => item.variant_id.toString() === this.variantId);
        if (isVariant && isVariant.id.toString() === this.variantId) {
          // The variant is still in cart
          this.updatePricePerItem(isVariant.quantity);
        } else {
          // The variant was removed from cart, qty is 0
          this.updatePricePerItem(0);
        }
        // All items were removed from cart
      } else {
        this.updatePricePerItem(0);
      }
    });
  }

  disconnectedCallback() {
    if (this.updatePricePerItemUnsubscriber) {
      this.updatePricePerItemUnsubscriber();
    }
    if (this.variantIdChangedUnsubscriber) {
      this.variantIdChangedUnsubscriber();
    }
  }

  onInputChange() {
    this.updatePricePerItem();
  }

  updatePricePerItem(updatedCartQuantity) {
    if (this.input) {
      this.enteredQty = parseInt(this.input.value);
      this.step = parseInt(this.input.step);
    }

    // updatedCartQuantity is undefined when qty is updated on product page. We need to sum entered qty and current qty in cart.
    // updatedCartQuantity is not undefined when qty is updated in cart. We need to sum qty in cart and min qty for product.
    this.currentQtyForVolumePricing =
      updatedCartQuantity === undefined
        ? this.getCartQuantity(updatedCartQuantity) + this.enteredQty
        : this.getCartQuantity(updatedCartQuantity) + parseInt(this.step);

    if (this.classList.contains('variant-item__price-per-item')) {
      this.currentQtyForVolumePricing = this.getCartQuantity(updatedCartQuantity);
    }
    for (let pair of this.qtyPricePairs) {
      if (this.currentQtyForVolumePricing >= pair[0]) {
        const pricePerItemCurrent = document.querySelector(
          `price-per-item[id^="Price-Per-Item-${
            this.dataset.sectionId || this.dataset.variantId
          }"] .price-per-item span`,
        );
        this.classList.contains('variant-item__price-per-item')
          ? (pricePerItemCurrent.innerHTML = window.theme.quickOrderListStrings.each.replace('[money]', pair[1]))
          : (pricePerItemCurrent.innerHTML = pair[1]);
        break;
      }
    }
  }

  getCartQuantity(updatedCartQuantity) {
    return updatedCartQuantity || updatedCartQuantity === 0
      ? updatedCartQuantity
      : parseInt(this.input.dataset.cartQuantity);
  }

  getVolumePricingArray() {
    const volumePricing = document.getElementById(`Volume-${this.dataset.sectionId || this.dataset.variantId}`);
    this.qtyPricePairs = [];

    if (volumePricing) {
      volumePricing.querySelectorAll('li').forEach((li) => {
        const qty = parseInt(li.querySelector('span:first-child').textContent);
        const price = li.querySelector('span:not(:first-child):last-child').dataset.text;
        this.qtyPricePairs.push([qty, price]);
      });
    }
    this.qtyPricePairs.reverse();
  }
}

customElements.define('variant-selects', VariantSelects);
customElements.define('product-info', ProductInfo);
customElements.define('product-form', ProductForm);
customElements.define('quantity-input', QuantityInput);
customElements.define('price-per-item', PricePerItem);
